import type { AuctionState, AuctionCreatePayload, AuctionView } from "./auction-view.js";
import type { RaffleDraft, RafflePreview, RaffleLaunch } from "./community-raffle-manager.js";
import type { SlotsState, SlotsResult } from "./slots.js";
import type { GiveawaySummary } from "./giveaway-view.js";
import {
  buildGarageParkPayload,
  buildGarageRedeemPayload,
  buildSystemControlPayload,
  type PortalActionContext,
  type SystemControlAction
} from "./actions.js";
import type {
  AccessControlPreviewSettings,
  AmbientAiSettings,
  BlackjackAction,
  BlackjackConfigurationSummary,
  BlackjackHandResult,
  BlackjackState,
  RouletteBetType,
  RouletteConfigurationSummary,
  RouletteSpinResult,
  RouletteState,
  RaptorRaceConfigurationSummary,
  RaptorRaceState,
  EcosystemZoneSettings,
  AuditEventSummary,
  CaseGarageRestoreCandidateSummary,
  CaseParticipantProfileSummary,
  ConfigurationSnapshotSettings,
  ConfigurationSnapshotValidation,
  CustomAiMapSettings,
  DiscordLogRoutingSettings,
  DiscordPatreonRolePolicySettings,
  CrossSpeciesChatSettings,
  CorpseCleanupSettings,
  EventWheelConfigurationSummary,
  EventWheelSpinResult,
  EventWheelState,
  EventJoinCampaignSummary,
  EventRewardNotificationSummary,
  EventRewardLogs,
  SpecimenEggInventory,
  SpecimenEggSummary,
  StaffCredentialsSettings,
  StaffDutySettings,
  StaffPerformanceSummary,
  GameBridgeOperationQueueSummary,
  GameBridgeOperationSummary,
  GameServerLogSample,
  GrowthCentreReport,
  GameplayEventFilters,
  GameplayEventPage,
  GarageHistorySummary,
  GarageRecordSummary,
  GarageCapacityPolicySettings,
  WorkRewardPolicySettings,
  PointBalancePolicySettings,
  LedgerEntrySummary,
  LeaderboardPeriod,
  LeaderboardSummary,
  ModerationCaseSummary,
  ModerationPolicySettings,
  ModerationStrikeRoleActions,
  ModerationStrikeSummary,
  PlayerProfileSummary,
  PlayerModerationStrikeSummary,
  PlayerSearchMatch,
  PortalSnapshotResponse,
  PortalLiveMapSnapshot,
  PortalAnnouncementSummary,
  PortalAccessSettings,
  ScheduledGameAnnouncementSummary,
  RuleDefinitionSummary,
  RuntimeReadinessSettings,
  ReleaseCentreSettings,
  ServerHealthSettings,
  TelemetryHealthSettings,
  TelemetryRetentionSettings,
  SkinReviewPresetSummary,
  SupportTicketMessageSummary,
  SupportTicketSummary,
  SupportTicketDiscordRoomSummary,
  SupportTicketMetricsSummary,
  SupportTicketAccusedPlayerSummary,
  SupportTicketEvidenceSummary,
  SupportTicketLogBundleSummary,
  SupportTicketLogEventSummary,
  StaffTicketNotificationPreferenceSummary,
  StaffTicketNotificationSummary,
  SupportTicketTranscriptSummary,
  SupportTicketWatcherSummary,
  SupportTicketRuleSummary
} from "./types.js";

export interface SupportTicketCapabilities {
  evidenceUploads: {
    enabled: boolean;
    maxBytes: number;
    maxFilesPerSubmission: number;
    extensions: string[];
  };
}

export interface StickTransferStatus {
  limit: number;
  sent: number;
  remaining: number;
  windowHours: number;
}

export interface StickTransferRecipient {
  userId: string;
  name: string;
}

export interface StickTransferResult {
  amount: number;
  recipient: StickTransferRecipient;
  wallet: { points: number; premium: number };
  remaining: number;
}

export interface PortalApiClient {
  loadSnapshot(userId: string, roleIds: string[]): Promise<PortalSnapshotResponse>;
  refreshPortalSession(): Promise<void>;
  loadLiveMap(): Promise<PortalLiveMapSnapshot>;
  getLeaderboard(serverId: string, period: LeaderboardPeriod): Promise<LeaderboardSummary>;
  listPortalAnnouncements(): Promise<PortalAnnouncementSummary[]>;
  getGiveaway(userId: string): Promise<{ giveaway: GiveawaySummary | null; serverNow: string }>;
  enterGiveaway(giveawayId: string, userId: string): Promise<{ entry: { id: string; enteredAt: string }; alreadyEntered: boolean }>;
  publishGiveaway(payload: { requestId: string; title: string; prize: string; description: string; closesAt: string; actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  cancelGiveaway(giveawayId: string, payload: { reason: string; actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  previewCommunityRaffle(payload: RaffleDraft): Promise<RafflePreview>;
  launchCommunityRaffle(payload: RaffleLaunch): Promise<unknown>;
  archiveGiveaway(giveawayId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  publishPortalAnnouncement(payload: { message: string; expiresInMinutes: number | null; actorUserId: string; actorDiscordRoleIds: string[] }): Promise<PortalAnnouncementSummary>;
  endPortalAnnouncement(announcementId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  listScheduledGameAnnouncements(): Promise<ScheduledGameAnnouncementSummary[]>;
  sendGameAnnouncement(payload: { message: string; actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  createScheduledGameAnnouncement(payload: { message: string; intervalMinutes: number; startsInMinutes: number; actorUserId: string; actorDiscordRoleIds: string[] }): Promise<ScheduledGameAnnouncementSummary>;
  disableScheduledGameAnnouncement(scheduleId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[] }): Promise<void>;
  createSkinPreset(payload: CreateSkinPresetPayload): Promise<{ preset: PortalSnapshotResponse["snapshot"]["skinPresets"][number] }>;
  loadPackLivery(code: string): Promise<{ livery: PackLiveryPayload }>;
  applySkinColors(
    context: PortalActionContext,
    expectedSpecies: string,
    expectedSkinTargetToken: string | undefined,
    customizer: Pick<CreateSkinPresetPayload, "female" | "patternIndex" | "themeIndex" | "goreIntensity" | "effectMode" | "effectColor1" | "effectColor2" | "effectColor3" | "effectSpeedSeconds" | "effectPattern" | "environmentMode" | "animation" | "colors">
  ): Promise<{ operation: GarageBridgeActionResult["operation"] }>;
  applySkinPreset(context: PortalActionContext, presetId: string, expectedSpecies: string, expectedSkinTargetToken?: string): Promise<{ operation: GarageBridgeActionResult["operation"] }>;
  applySkinPalette(context: PortalActionContext, presetId: string, expectedSpecies: string, expectedSkinTargetToken?: string): Promise<{ operation: GarageBridgeActionResult["operation"] }>;
  deleteSkinPreset(context: PortalActionContext, presetId: string): Promise<void>;
  listSkinPresetsForReview(status: "pending_review" | "approved"): Promise<SkinReviewPresetSummary[]>;
  reviewSkinPreset(presetId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[]; decision: "approved" | "rejected" | "disabled"; reason: string }): Promise<{ preset: SkinReviewPresetSummary }>;
  parkLiveDino(context: PortalActionContext): Promise<GarageBridgeActionResult>;
  observeLiveDino(context: PortalActionContext, requestId: string): Promise<GarageBridgeActionResult>;
  slayLiveDino(context: PortalActionContext, requestId: string): Promise<GarageBridgeActionResult>;
  requestCorpseDrop(context: PortalActionContext, catalogItemId?: string): Promise<CorpseDropPortalResult>;
  redeemGarageDino(context: PortalActionContext, garageDinoId: string): Promise<GarageBridgeActionResult>;
  sellGarageDino(context: PortalActionContext, garageDinoId: string, reason: string): Promise<void>;
  discardGarageDino(context: PortalActionContext, garageDinoId: string, reason: string): Promise<void>;
  renameGarageDino(context: PortalActionContext, garageDinoId: string, displayName: string): Promise<void>;
  buyMarketplaceListing(context: PortalActionContext, listingId: string): Promise<void>;
  getAuctions(userId: string): Promise<AuctionState>;
  createAuction(payload: AuctionCreatePayload): Promise<{ auction: AuctionView }>;
  bidAuction(auctionId: string, payload: { userId: string; requestId: string; amount: number; buyNow: boolean }): Promise<unknown>;
  auctionAction(auctionId: string, action: "cancel" | "collect" | "proceeds", userId: string): Promise<{ claimed?: number }>;
  createMarketplaceDinoListing(context: PortalActionContext, garageDinoId: string, payload: CreateMarketplaceDinoListingPayload): Promise<void>;
  cancelMarketplaceListing(context: PortalActionContext, listingId: string, reason: string): Promise<void>;
  enterCommunityRaffle(userId: string, raffleId: string): Promise<{ alreadyEntered: boolean }>;
  buyStoreItem(context: PortalActionContext, catalogItemId: string, requestId: string, mutations?: string[]): Promise<{ operation?: GarageBridgeActionResult["operation"] }>;
  getFriendTeleportCode(userId: string): Promise<FriendTeleportCodeState>;
  createFriendTeleportCode(userId: string): Promise<FriendTeleportCodeState>;
  redeemFriendTeleportCode(userId: string, code: string): Promise<{ operation: GarageBridgeActionResult["operation"]; cost: number }>;
  saveCorpseProduct(context: PortalActionContext, catalogItemId: string, payload: SaveCorpseProductPayload): Promise<void>;
  saveStoreProduct(context: PortalActionContext, catalogItemId: string, payload: SaveStoreProductPayload): Promise<void>;
  claimWorkReward(context: PortalActionContext): Promise<WorkRewardResult>;
  getStickTransferStatus(userId: string): Promise<StickTransferStatus>;
  searchStickTransferRecipients(userId: string, query: string): Promise<StickTransferRecipient[]>;
  sendSticks(payload: { senderUserId: string; recipientUserId: string; requestId: string; amount: number; note?: string }): Promise<StickTransferResult>;
  getEventWheel(userId: string, serverId: string): Promise<EventWheelState>;
  spinEventWheel(payload: { userId: string; serverId: string; requestId: string }): Promise<EventWheelSpinResult>;
  claimPrimeToken(spinId: string, payload: { userId: string; serverId: string; requestId: string }): Promise<{ operation?: GarageBridgeActionResult["operation"]; applied?: boolean; pending?: boolean; replayed?: boolean }>;
  getSpecimenEggs(userId: string, serverId: string): Promise<SpecimenEggInventory>;
  claimSpecimenEgg(spinId: string, payload: { userId: string; serverId: string; requestId: string; species: string; mutations: string[] }): Promise<{ egg: SpecimenEggSummary; garageDino: GarageRecordSummary; replayed: boolean }>;
  grantEventCredits(payload: { actorUserId: string; targetUserId: string; serverId: string; requestId: string; amount: number; reason: string }): Promise<{ balance: number; playerName: string }>;
  grantStaffPoints(payload: { actorUserId: string; actorDiscordRoleIds: string[]; targetUserId: string; requestId: string; amount: number; reason: string }): Promise<{ wallet: { points: number; premium: number }; awardedAmount: number; playerName: string }>;
  getEventWheelConfig(serverId: string): Promise<EventWheelConfigurationSummary>;
  saveEventWheelConfig(payload: Omit<EventWheelConfigurationSummary, "updatedAt"> & { actorUserId: string; reason: string }): Promise<EventWheelConfigurationSummary>;
  getEventJoinCampaign(serverId: string): Promise<{ campaign?: EventJoinCampaignSummary; active: boolean; canManageBoosts?: boolean; stagedGrowthMultiplier?: number }>;
  saveEventJoinCampaign(payload: Omit<EventJoinCampaignSummary, "updatedAt"> & { actorUserId: string; reason: string }): Promise<{ campaign: EventJoinCampaignSummary; active: boolean; stagedGrowthMultiplier?: number; growthStageError?: string }>;
  getEventRewardLogs(serverId: string, limit?: number): Promise<EventRewardLogs>;
  getBlackjack(userId: string, serverId: string): Promise<BlackjackState>;
  dealBlackjack(payload: { userId: string; serverId: string; requestId: string; wager: number }): Promise<BlackjackHandResult>;
  playBlackjack(payload: { handId: string; userId: string; serverId: string; requestId: string; expectedVersion: number; action: BlackjackAction }): Promise<BlackjackHandResult>;
  getBlackjackConfig(serverId: string): Promise<BlackjackConfigurationSummary>;
  saveBlackjackConfig(payload: Omit<BlackjackConfigurationSummary, "updatedAt" | "updatedByUserId"> & { actorUserId: string; reason: string }): Promise<BlackjackConfigurationSummary>;
  getSlots(userId: string, serverId: string): Promise<SlotsState>;
  spinSlots(payload: { userId: string; serverId: string; requestId: string; wager: number }): Promise<SlotsResult>;
  getSlotsConfig(serverId: string): Promise<RouletteConfigurationSummary>;
  saveSlotsConfig(payload: Omit<RouletteConfigurationSummary, "updatedAt" | "updatedByUserId"> & { actorUserId: string; reason: string }): Promise<RouletteConfigurationSummary>;
  getRoulette(userId: string, serverId: string): Promise<RouletteState>;
  spinRoulette(payload: { userId: string; serverId: string; requestId: string; bets: Array<{ type: RouletteBetType; value?: string; wager: number }> }): Promise<RouletteSpinResult>;
  getRouletteConfig(serverId: string): Promise<RouletteConfigurationSummary>;
  saveRouletteConfig(payload: Omit<RouletteConfigurationSummary, "updatedAt" | "updatedByUserId"> & { actorUserId: string; reason: string }): Promise<RouletteConfigurationSummary>;
  getRaptorRace(userId: string, serverId: string): Promise<RaptorRaceState>;
  placeRaptorRaceBet(payload: { userId: string; serverId: string; requestId: string; raceId: string; entrantId: string; wager: number }): Promise<RaptorRaceState>;
  getRaptorRaceConfig(serverId: string): Promise<RaptorRaceConfigurationSummary>;
  saveRaptorRaceConfig(payload: Omit<RaptorRaceConfigurationSummary, "updatedAt" | "updatedByUserId"> & { actorUserId: string; reason: string }): Promise<RaptorRaceConfigurationSummary>;
  createBugReport(payload: CreateBugReportPayload): Promise<BugReportResult>;
  disconnectDiscord(): Promise<void>;
  logout(): Promise<void>;
  listGarageDinos(userId: string): Promise<GarageRecordSummary[]>;
  getGarageHistory(userId: string): Promise<GarageHistorySummary>;
  restoreGarageDino(payload: GarageRestorePayload): Promise<void>;
  restoreDeathSnapshot(deathEventId: string, payload: { targetUserId: string; actorUserId: string; actorDiscordRoleIds: string[]; reason: string; ticketId?: string }): Promise<void>;
  migrateGarageDino(payload: GarageMigrationPayload): Promise<{ garageDino: GarageRecordSummary; duplicate: boolean }>;
  inspectDinoMutations(payload: MutationInspectionPayload): Promise<{ operation: GarageBridgeActionResult["operation"] }>;
  correctDinoMutation(payload: MutationCorrectionPayload): Promise<{ operation: GarageBridgeActionResult["operation"]; queuedUntilOnline?: boolean }>;
  requestSystemControl(context: PortalActionContext, action: SystemControlAction, reason: string): Promise<void>;
  getProfileByDiscordUserId(discordUserId: string): Promise<PlayerProfileSummary>;
  getProfileByLookup(lookup: string): Promise<PlayerProfileSummary>;
  searchProfiles(query: string): Promise<PlayerSearchMatch[]>;
  resetCorpseCooldown(payload: { targetUserId: string; actorUserId: string; actorDiscordRoleIds: string[]; serverId: string; reason: string }): Promise<void>;
  listModerationCases(userId: string, roleIds: string[]): Promise<ModerationCaseSummary[]>;
  listMyModerationStrikes(userId: string): Promise<PlayerModerationStrikeSummary[]>;
  listModerationStrikes(userId: string, roleIds: string[]): Promise<{
    strikes: ModerationStrikeSummary[];
    roleActions?: ModerationStrikeRoleActions;
  }>;
  createModerationCase(payload: CreateModerationCasePayload): Promise<void>;
  createModerationStrike(payload: CreateModerationStrikePayload): Promise<{
    roleActions?: ModerationStrikeRoleActions;
    discordRoleSync?: { applied: string[]; removed: string[]; errors: string[] };
    discordNotice?: { delivered: boolean; error?: string };
  }>;
  resolveModerationStrike(strikeId: string, payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    status: "forgiven" | "voided";
    reason: string;
    surface: "website";
  }): Promise<{ strike: ModerationStrikeSummary; discordNotice?: { delivered: boolean; error?: string } }>;
  updateWeather(serverId: string, payload: UpdateWeatherPayload): Promise<void>;
  listLedgerEntries(userId: string, roleIds: string[], limit?: number): Promise<{
    wallet: { points: number; premium: number };
    entries: LedgerEntrySummary[];
  }>;
  listGameServerLogSamples(roleIds: string[], limit?: number): Promise<GameServerLogSample[]>;
  listAuditEvents(roleIds: string[], options?: { type?: string; targetId?: string; limit?: number }): Promise<AuditEventSummary[]>;
  getDiscordLogRouting(roleIds: string[]): Promise<DiscordLogRoutingSettings>;
  saveDiscordLogRoute(payload: { id?: string; serverId: string; channelId: string; categories: string[]; enabled: boolean; actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<void>;
  deleteDiscordLogRoute(routeId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<void>;
  previewDiscordLogRoute(payload: { channelId: string; categories: string[]; actorDiscordRoleIds: string[] }): Promise<void>;
  getCrossSpeciesChatConfig(roleIds: string[]): Promise<CrossSpeciesChatSettings>;
  saveCrossSpeciesChatConfig(payload: CrossSpeciesChatSettings["config"] & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<CrossSpeciesChatSettings>;
  getAmbientAiConfig(roleIds: string[]): Promise<AmbientAiSettings>;
  saveAmbientAiConfig(payload: AmbientAiSettings["config"] & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<AmbientAiSettings>;
  getCorpseCleanupConfig(roleIds: string[]): Promise<CorpseCleanupSettings>;
  saveCorpseCleanupConfig(payload: Pick<CorpseCleanupSettings["config"], "enabled" | "intervalHours" | "warningMinutes"> & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<CorpseCleanupSettings>;
  getEcosystemZones(roleIds: string[]): Promise<EcosystemZoneSettings>;
  saveEcosystemZones(payload: EcosystemZoneSettings["config"] & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<EcosystemZoneSettings>;
  getStaffCredentials(roleIds: string[]): Promise<StaffCredentialsSettings>;
  saveStaffCredentials(payload: Pick<StaffCredentialsSettings["config"], "enabled" | "developerSteamIds"> & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<StaffCredentialsSettings>;
  getModerationPolicy(roleIds: string[]): Promise<ModerationPolicySettings>;
  saveModerationPolicy(payload: {
    strikeOneRoleId: string | null;
    strikeTwoRoleId: string | null;
    strikeThreeRoleId: string | null;
    escalationRoleIds: string[];
    playtimeRewardBlockAtWeight: 1 | 2 | 3 | null;
    ladder: ModerationPolicySettings["policy"]["ladder"];
    actorUserId: string;
    actorDiscordRoleIds: string[];
    reason: string;
  }): Promise<ModerationPolicySettings>;
  getGarageCapacityPolicy(roleIds: string[]): Promise<GarageCapacityPolicySettings>;
  saveGarageCapacityPolicy(payload: {
    defaultPerSpecies: number;
    patreonPerSpecies: number;
    staffPerSpecies: number;
    tierLimits: Array<{ tierName: string; perSpecies: number }>;
    actorUserId: string;
    actorDiscordRoleIds: string[];
    reason: string;
  }): Promise<GarageCapacityPolicySettings>;
  saveWorkRewardPolicy(payload: {
    minPoints: number;
    maxPoints: number;
    cooldownSeconds: number;
    actorUserId: string;
    actorDiscordRoleIds: string[];
    reason: string;
  }): Promise<WorkRewardPolicySettings>;
  getPointBalancePolicy(roleIds: string[]): Promise<PointBalancePolicySettings>;
  savePointBalancePolicy(payload: {
    defaultMaxPoints: number;
    patreonMaxPoints: number;
    staffMaxPoints: number;
    tierLimits: Array<{ tierName: string; maxPoints: number }>;
    actorUserId: string;
    actorDiscordRoleIds: string[];
    reason: string;
  }): Promise<PointBalancePolicySettings>;
  getDiscordPatreonRolePolicy(roleIds: string[]): Promise<DiscordPatreonRolePolicySettings>;
  saveDiscordPatreonRolePolicy(payload: {
    mappings: Array<{ roleId: string; tierName: string; priority: number }>;
    actorUserId: string;
    actorDiscordRoleIds: string[];
    reason: string;
  }): Promise<DiscordPatreonRolePolicySettings>;
  getRuntimeReadiness(): Promise<RuntimeReadinessSettings>;
  getServerHealth(serverId: string, roleIds: string[]): Promise<ServerHealthSettings>;
  getReleaseCentre(): Promise<ReleaseCentreSettings>;
  getGrowthCentre(days: 7 | 30 | 90): Promise<GrowthCentreReport>;
  saveReleaseSignoff(payload: { checkKey: string; result: "passed" | "failed" | "blocked"; notes: string }): Promise<ReleaseCentreSettings>;
  getCustomAiMap(serverId: string, roleIds: string[]): Promise<CustomAiMapSettings>;
  getTelemetryHealth(serverId: string, roleIds: string[]): Promise<TelemetryHealthSettings>;
  listGameplayEvents(serverId: string, roleIds: string[], filters?: GameplayEventFilters): Promise<GameplayEventPage>;
  getTelemetryRetention(serverId: string, roleIds: string[]): Promise<TelemetryRetentionSettings>;
  saveTelemetryRetention(payload: TelemetryRetentionSettings & { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<TelemetryRetentionSettings>;
  getAccessControlPreview(roleIds: string[]): Promise<AccessControlPreviewSettings>;
  getPortalAccessSettings(roleIds: string[]): Promise<PortalAccessSettings>;
  savePortalAccessSettings(payload: { enabled: boolean; actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<PortalAccessSettings>;
  exportConfigurationSnapshot(context: PortalActionContext): Promise<ConfigurationSnapshotSettings>;
  validateConfigurationSnapshot(context: PortalActionContext, snapshot: ConfigurationSnapshotSettings): Promise<ConfigurationSnapshotValidation>;
  getStaffDuty(actorUserId: string, roleIds: string[]): Promise<StaffDutySettings>;
  getStaffPerformance(actorUserId: string, roleIds: string[], options: { periodDays: number; from?: string; to?: string; staffUserId?: string }): Promise<StaffPerformanceSummary>;
  setStaffDuty(payload: { actorUserId: string; actorDiscordRoleIds: string[]; onDuty: boolean; reason: string }): Promise<StaffDutySettings>;
  listGameBridgeOperations(roleIds: string[], options?: { status?: string[]; limit?: number }): Promise<{
    operations: GameBridgeOperationSummary[];
    summary?: GameBridgeOperationQueueSummary;
  }>;
  getGameBridgeOperation(operationId: string, userId: string): Promise<GameBridgeOperationSummary | undefined>;
  resolveGameBridgeOperation(operationId: string, payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    status: "confirmed" | "failed" | "manual_review";
    reason: string;
  }): Promise<GameBridgeOperationSummary>;
  listSupportTickets(roleIds: string[], options?: {
    actorUserId?: string;
    query?: string;
    type?: string;
    status?: string;
    lifecycle?: "active" | "closed" | "all";
    priority?: string;
    claimedByUserId?: string;
    creatorUserId?: string;
    accused?: string;
    ruleCode?: string;
    tag?: string;
    resolutionType?: string;
    createdFrom?: string;
    createdTo?: string;
    unassigned?: boolean;
    overdue?: boolean;
    needsFollowup?: boolean;
    cursor?: string;
    limit?: number;
  }): Promise<{ tickets: SupportTicketSummary[]; nextCursor: string | null }>;
  updateSupportTicketTags(ticketId: string, payload: StaffTicketActionPayload & { tags: string[] }): Promise<SupportTicketSummary>;
  bulkUpdateSupportTickets(payload: StaffTicketActionPayload & {
    ticketIds: string[];
    action: "assign" | "add_tags";
    assigneeUserId?: string;
    tags?: string[];
  }): Promise<{ requested: number; updated: number; unchangedOrUnavailable: number }>;
  getSupportTicketMetrics(roleIds: string[], actorUserId: string, periodDays?: number): Promise<SupportTicketMetricsSummary>;
  getStaffTicketNotificationPreferences(actorUserId: string, roleIds: string[]): Promise<StaffTicketNotificationPreferenceSummary>;
  updateStaffTicketNotificationPreferences(payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    inAppEnabled: boolean;
    discordDmEnabled: boolean;
    notifyAssignment: boolean;
    notifyWatchedCase: boolean;
    notifyReporterReply: boolean;
  }): Promise<StaffTicketNotificationPreferenceSummary>;
  listStaffTicketNotifications(actorUserId: string, roleIds: string[], limit?: number): Promise<{ unreadCount: number; notifications: StaffTicketNotificationSummary[] }>;
  markStaffTicketNotificationRead(notificationId: string, actorUserId: string, roleIds: string[]): Promise<void>;
  markAllStaffTicketNotificationsRead(actorUserId: string, roleIds: string[]): Promise<void>;
  listEventRewardNotifications(userId: string, serverId: string, limit?: number): Promise<{ unreadCount: number; notifications: EventRewardNotificationSummary[] }>;
  markEventRewardNotificationRead(notificationId: string, userId: string): Promise<void>;
  markAllEventRewardNotificationsRead(userId: string, serverId: string): Promise<void>;
  listMySupportTickets(actorUserId: string, limit?: number): Promise<SupportTicketSummary[]>;
  getSupportTicketCapabilities(): Promise<SupportTicketCapabilities>;
  createSupportTicket(payload: {
    type: "support" | "rule_question" | "player_report" | "ban_appeal" | "staff_report" | "other";
    creatorUserId: string;
    subject: string;
    body: string;
    targetSteam64Id?: string;
    appealedStrikeId?: string;
    evidenceUrl?: string;
    priority?: "normal" | "urgent";
    submissionKey?: string;
  }): Promise<SupportTicketSummary>;
  createStaffContactTicket(payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    targetUserId: string;
    subject: string;
    body: string;
    submissionKey: string;
  }): Promise<SupportTicketSummary>;
  getSupportTicket(ticketId: string, actorUserId: string, roleIds: string[]): Promise<{
    ticket: SupportTicketSummary;
    messages: SupportTicketMessageSummary[];
    accusedPlayers: SupportTicketAccusedPlayerSummary[];
    reporterProfile?: CaseParticipantProfileSummary;
    claimedByDisplayName?: string;
    accusedProfiles?: CaseParticipantProfileSummary[];
    garageRestoreCandidates: CaseGarageRestoreCandidateSummary[];
    canSendToOwner: boolean;
    canViewOwnerCases: boolean;
    evidence: SupportTicketEvidenceSummary[];
    appealedStrike?: PlayerModerationStrikeSummary;
    logBundles: SupportTicketLogBundleSummary[];
    evidenceUploadsEnabled: boolean;
    watchers: SupportTicketWatcherSummary[];
    rules: SupportTicketRuleSummary[];
    transcript?: SupportTicketTranscriptSummary | null;
    discordRoom: SupportTicketDiscordRoomSummary;
  }>;
  openSupportTicketDiscordRoom(ticketId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[]; reason: string }): Promise<{ channelId: string; url?: string; replayed: boolean }>;
  addSupportTicketEvidence(ticketId: string, payload: {
    authorUserId: string;
    actorDiscordRoleIds: string[];
    label: string;
    url: string;
  }): Promise<void>;
  uploadSupportTicketEvidence(ticketId: string, label: string, file: File): Promise<void>;
  reviewSupportTicketEvidence(ticketId: string, evidenceId: string, payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    status: "reviewed" | "relevant" | "inconclusive" | "rejected";
    reason?: string;
  }): Promise<void>;
  createSupportTicketLogBundle(ticketId: string, payload: StaffTicketActionPayload & {
    participantRole: "reporter" | "accused";
    accusedId?: string;
    windowStart: string;
    windowEnd: string;
  }): Promise<void>;
  getSupportTicketLogBundle(ticketId: string, bundleId: string): Promise<{
    logBundle: SupportTicketLogBundleSummary;
    events: SupportTicketLogEventSummary[];
  }>;
  attachSupportTicketLogEvidence(ticketId: string, bundleId: string, payload: StaffTicketActionPayload & {
    label: string;
    eventIds: string[];
  }): Promise<void>;
  addSupportTicketWatcher(ticketId: string, payload: StaffTicketActionPayload & { watcherUserId: string }): Promise<void>;
  removeSupportTicketWatcher(ticketId: string, payload: StaffTicketActionPayload & { watcherUserId: string }): Promise<void>;
  listRuleDefinitions(roleIds: string[], options?: { query?: string; section?: string; status?: "draft" | "published" | "retired" }): Promise<RuleDefinitionSummary[]>;
  createRuleDefinition(payload: { actorUserId: string; actorDiscordRoleIds: string[]; reason: string; code: string; section: string; title: string; text: string; keywords: string[] }): Promise<RuleDefinitionSummary>;
  reviseRuleDefinition(ruleId: string, payload: { actorUserId: string; actorDiscordRoleIds: string[]; reason: string; section?: string; title?: string; text?: string; keywords?: string[] }): Promise<RuleDefinitionSummary>;
  changeRuleDefinitionStatus(ruleId: string, action: "publish" | "retire", payload: StaffTicketActionPayload): Promise<RuleDefinitionSummary>;
  attachSupportTicketRule(ticketId: string, payload: StaffTicketActionPayload & { ruleId: string }): Promise<void>;
  reviewSupportTicketRule(ticketId: string, ticketRuleId: string, payload: StaffTicketActionPayload & { outcome: "upheld" | "not_upheld" | "inconclusive"; reviewReason?: string }): Promise<void>;
  attachSupportTicketAccused(ticketId: string, payload: {
    actorUserId: string;
    actorDiscordRoleIds: string[];
    targetUserId: string;
    reason: string;
    confirmReporterOverride: boolean;
  }): Promise<void>;
  removeSupportTicketAccused(ticketId: string, accusedId: string, payload: StaffTicketActionPayload): Promise<void>;
  getSupportTicketTranscript(ticketId: string, actorUserId: string, roleIds: string[]): Promise<{
    ticket: SupportTicketSummary;
    transcript: SupportTicketTranscriptSummary;
  }>;
  replyToSupportTicket(ticketId: string, payload: {
    authorUserId: string;
    actorDiscordRoleIds: string[];
    body: string;
    internal: boolean;
  }): Promise<{ ticket: SupportTicketSummary; message: SupportTicketMessageSummary }>;
  claimSupportTicket(ticketId: string, payload: StaffTicketActionPayload): Promise<void>;
  updateSupportTicketOperations(ticketId: string, payload: StaffTicketActionPayload & {
    claimedByUserId?: string | null;
    priority?: "low" | "normal" | "high" | "urgent";
    dueAt?: string | null;
  }): Promise<void>;
  updateSupportTicketStatus(ticketId: string, payload: StaffTicketActionPayload & { status: string; escalationReason?: string }): Promise<void>;
  closeSupportTicket(ticketId: string, payload: StaffTicketActionPayload & {
    resolutionType?: string;
    closureNote?: string;
    reporterClosureMessage?: string;
    moderation?: {
      targetUserId: string;
      level: "warning" | "strike_1" | "strike_2" | "strike_3" | "strike_4";
      reason: string;
      requestId: string;
    };
    compensation?: {
      targetUserId: string;
      amount: number;
      reason: string;
      requestId: string;
    };
  }): Promise<{
    ticket: SupportTicketSummary;
    moderation?: {
      strike: ModerationStrikeSummary;
      discordRoleSync: { applied: string[]; removed: string[]; errors: string[] };
      discordNotice: { delivered: boolean; error?: string };
    };
    compensation?: {
      ledgerEntry: { id: string; userId: string; amount: number; reason: string };
      requestedAmount: number;
      awardedAmount: number;
    };
  }>;
}

export interface GarageBridgeActionResult {
  garageDino?: {
    id: string;
    displayName: string;
    species: string;
    status: string;
  };
  operation: {
    id: string;
    type: string;
    status: string;
  };
}

export interface CorpseDropPortalResult {
  request: {
    id: string;
    status: string;
    cooldownEndsAt?: string;
    denialReasons?: string[];
  };
  eligibility: {
    eligible: boolean;
    reasons: string[];
    nextAvailableAt?: string;
  };
  operation?: {
    id: string;
    type: string;
    status: string;
  };
}

export interface FriendTeleportCodeState {
  code: { code: string; expiresAt: string } | null;
  cooldownEndsAt: string | null;
}

export interface SaveCorpseProductPayload {
  name: string;
  species: string;
  growth: number;
  price: number;
  stock: number | null;
  enabled: boolean;
  patreonDiscountEligible: boolean;
  reason: string;
}

export interface SaveStoreProductPayload {
  type: "dino_growth" | "corpse_drop" | "teleport";
  name: string;
  species?: string;
  growth?: number;
  price: number;
  stock: number | null;
  enabled: boolean;
  patreonDiscountEligible: boolean;
  description?: string;
  destination?: string;
  x?: number;
  y?: number;
  z?: number;
  yaw?: number;
  maxGrowth?: number;
  resolveGround?: boolean;
  scatterRadius?: number;
  minimumScatterRadius?: number;
  reason: string;
}

export interface CreateModerationCasePayload {
  type: string;
  targetUserId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  reason: string;
  evidenceUrl?: string;
  duration?: string;
  surface: "website";
}

export interface CreateModerationStrikePayload {
  targetUserId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  reason: string;
  evidenceUrl?: string;
  supportTicketId?: string;
  duration?: string;
  weight: number;
  level?: "warning" | "strike_1" | "strike_2" | "strike_3" | "strike_4";
  surface: "website";
  requestId: string;
}

export interface UpdateWeatherPayload {
  condition: string;
  intensity?: number;
  temperatureC?: number;
  windKph?: number;
  forecastNote?: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  reason: string;
}

export interface CreateMarketplaceDinoListingPayload {
  buyNowPrice?: number;
  durationHours: number;
}

export interface WorkRewardResult {
  ledgerEntry: {
    id: string;
    amount: number;
  };
  wallet: {
    points: number;
    premium: number;
  };
  cooldownEndsAt: string;
}

export interface CreateBugReportPayload {
  creatorUserId: string;
  summary: string;
  details: string;
  steps?: string;
  page: string;
}

export interface BugReportResult {
  ticketId: string;
  discordDelivery: "delivered" | "not_configured" | "failed";
}

export interface StaffTicketActionPayload {
  actorUserId: string;
  actorDiscordRoleIds: string[];
  reason: string;
}

export class PortalApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "PortalApiError";
  }
}

export function isPortalAuthenticationError(error: unknown): boolean {
  return error instanceof PortalApiError && (error.status === 401 || error.status === 403);
}

export interface CreateSkinPresetPayload {
  userId: string;
  actorDiscordRoleIds: string[];
  mode?: "live" | "offline";
  expectedSpecies?: string;
  expectedServerId?: string;
  expectedSkinTargetToken?: string;
  species: string;
  name: string;
  female: boolean;
  skinVariation: number;
  patternIndex: number;
  themeIndex: number;
  goreIntensity: number;
  effectMode: import("./types.js").SkinEffectMode;
  effectColor1: string;
  effectColor2: string;
  effectColor3: string;
  effectSpeedSeconds: number;
  effectPattern: import("./types.js").SkinEffectPattern;
  environmentMode: import("./types.js").SkinEnvironmentMode;
  animation?: import("./types.js").SkinAnimation;
  packLivery: boolean;
  colors: Record<string, { r: number; g: number; b: number; a: number }>;
  restricted: boolean;
}

export interface PackLiveryPayload {
  name: string;
  species: string;
  female: boolean;
  patternIndex: number;
  themeIndex: number;
  goreIntensity: number;
  effectMode: import("./types.js").SkinEffectMode;
  effectColor1: string;
  effectColor2: string;
  effectColor3: string;
  effectSpeedSeconds: number;
  effectPattern: import("./types.js").SkinEffectPattern;
  environmentMode: import("./types.js").SkinEnvironmentMode;
  animation?: import("./types.js").SkinAnimation;
  colors: Record<string, { r: number; g: number; b: number; a: number }>;
}

export interface GarageRestorePayload {
  targetUserId: string;
  garageDinoId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  reason: string;
  requestId: string;
  ticketId?: string;
}

export interface GarageMigrationPayload {
  targetUserId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  requestId: string;
  species: string;
  displayName: string;
  growth: number;
  health: number;
  stamina: number;
  hunger: number;
  thirst: number;
  primeActive: boolean;
  mutations: string[];
  position?: { latitude: number; longitude: number; altitude?: number };
  sourceReference: string;
  reason: string;
}

export interface MutationCorrectionPayload {
  targetUserId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  mode: "live" | "offline" | "stored";
  garageDinoId?: string;
  mutation: string;
  targetSlot: string;
  expectedMutation: string;
  reason: string;
}

export interface MutationInspectionPayload {
  targetUserId: string;
  actorUserId: string;
  actorDiscordRoleIds: string[];
  mode: "live" | "stored";
  garageDinoId?: string;
  reason: string;
}

export function createPortalApiClient(baseUrl = ""): PortalApiClient {
  return {
    async listPortalAnnouncements() {
      const body = await getJson<{ announcements?: PortalAnnouncementSummary[] }>(`${baseUrl}/api/announcements`);
      return body.announcements ?? [];
    },
    async getGiveaway(userId) {
      return getJson(`${baseUrl}/api/giveaways/current?userId=${encodeURIComponent(userId)}`);
    },
    async enterGiveaway(giveawayId, userId) {
      return postJson(`${baseUrl}/api/giveaways/${encodeURIComponent(giveawayId)}/entries`, { userId });
    },
    async publishGiveaway(payload) {
      await postJson(`${baseUrl}/api/giveaways`, payload);
    },
    async cancelGiveaway(giveawayId, payload) {
      await postJson(`${baseUrl}/api/giveaways/${encodeURIComponent(giveawayId)}/cancel`, payload, [204]);
    },
    async previewCommunityRaffle(payload) { return postJson(`${baseUrl}/api/community-raffles/preview`, payload); },
    async launchCommunityRaffle(payload) { return postJson(`${baseUrl}/api/community-raffles`, payload, [200, 201]); },
    async archiveGiveaway(giveawayId, payload) {
      await postJson(`${baseUrl}/api/giveaways/${encodeURIComponent(giveawayId)}/archive`, payload, [204]);
    },
    async publishPortalAnnouncement(payload) {
      const body = await postJson<{ announcement?: PortalAnnouncementSummary }>(`${baseUrl}/api/announcements`, payload);
      if (!body.announcement) throw new Error("Announcement response was missing announcement data.");
      return body.announcement;
    },
    async endPortalAnnouncement(announcementId, payload) {
      await postJson(`${baseUrl}/api/announcements/${encodeURIComponent(announcementId)}/end`, payload, [204]);
    },
    async listScheduledGameAnnouncements() {
      const body = await getJson<{ schedules?: ScheduledGameAnnouncementSummary[] }>(`${baseUrl}/api/announcements/game-schedules`);
      return body.schedules ?? [];
    },
    async sendGameAnnouncement(payload) {
      await postJson(`${baseUrl}/api/announcements/game`, payload, [202]);
    },
    async createScheduledGameAnnouncement(payload) {
      const body = await postJson<{ schedule?: ScheduledGameAnnouncementSummary }>(`${baseUrl}/api/announcements/game-schedules`, payload);
      if (!body.schedule) throw new Error("Schedule response was missing schedule data.");
      return body.schedule;
    },
    async disableScheduledGameAnnouncement(scheduleId, payload) {
      await postJson(`${baseUrl}/api/announcements/game-schedules/${encodeURIComponent(scheduleId)}/disable`, payload, [204]);
    },
    async loadSnapshot(userId, roleIds) {
      return postJson<PortalSnapshotResponse>(`${baseUrl}/api/portal/snapshot`, {
        ...(userId ? { userId } : {}),
        roleIds
      });
    },
    async refreshPortalSession() {
      await postJson(`${baseUrl}/api/portal/session/refresh`, {});
    },
    async loadLiveMap() {
      return getJson<PortalLiveMapSnapshot>(`${baseUrl}/api/portal/live-map`, { timeoutMs: 15_000 });
    },
    async getLeaderboard(serverId, period) {
      const body = await getJson<{ leaderboard: LeaderboardSummary }>(`${baseUrl}/api/leaderboards/${encodeURIComponent(serverId)}?period=${encodeURIComponent(period)}`);
      return body.leaderboard;
    },
    async createSkinPreset(payload) {
      return postJson(`${baseUrl}/api/skins`, payload);
    },
    async loadPackLivery(code) {
      return getJson(`${baseUrl}/api/skins/livery/${encodeURIComponent(code)}`);
    },
    async applySkinColors(context, expectedSpecies, expectedSkinTargetToken, customizer) {
      return postJson(`${baseUrl}/api/skins/apply`, {
        userId: context.userId,
        actorDiscordRoleIds: context.roleIds,
        expectedSpecies,
        expectedServerId: context.serverId,
        ...(expectedSkinTargetToken ? { expectedSkinTargetToken } : {}),
        ...customizer
      });
    },
    async applySkinPreset(context, presetId, expectedSpecies, expectedSkinTargetToken) {
      return postJson(`${baseUrl}/api/skins/${encodeURIComponent(presetId)}/apply`, {
        userId: context.userId,
        actorDiscordRoleIds: context.roleIds,
        expectedSpecies,
        expectedServerId: context.serverId,
        ...(expectedSkinTargetToken ? { expectedSkinTargetToken } : {})
      });
    },
    async applySkinPalette(context, presetId, expectedSpecies, expectedSkinTargetToken) {
      return postJson(`${baseUrl}/api/skins/${encodeURIComponent(presetId)}/apply-palette`, {
        userId: context.userId,
        actorDiscordRoleIds: context.roleIds,
        expectedSpecies,
        expectedServerId: context.serverId,
        ...(expectedSkinTargetToken ? { expectedSkinTargetToken } : {})
      });
    },
    async listSkinPresetsForReview(status) {
      const query = new URLSearchParams({ status, limit: "50" });
      const body = await getJson<{ presets?: SkinReviewPresetSummary[] }>(`${baseUrl}/api/skins/review/pending?${query.toString()}`);
      return body.presets ?? [];
    },
    async deleteSkinPreset(context, presetId) {
      await deleteJson(`${baseUrl}/api/skins/${encodeURIComponent(presetId)}`, { userId: context.userId });
    },
    async reviewSkinPreset(presetId, payload) {
      return postJson(`${baseUrl}/api/skins/${encodeURIComponent(presetId)}/review`, payload);
    },
    async parkLiveDino(context) {
      return postJson<GarageBridgeActionResult>(`${baseUrl}/api/garage/park`, buildGarageParkPayload(context));
    },
    async observeLiveDino(context, requestId) {
      return postJson<GarageBridgeActionResult>(`${baseUrl}/api/live-dino/observe`, {
        userId: context.userId,
        requestId
      });
    },
    async slayLiveDino(context, requestId) {
      return postJson<GarageBridgeActionResult>(`${baseUrl}/api/live-dino/slay`, {
        userId: context.userId,
        requestId
      });
    },
    async requestCorpseDrop(context, catalogItemId) {
      const result = await postJson<CorpseDropPortalResult>(`${baseUrl}/api/corpse-drops/request`, {
        userId: context.userId,
        serverId: context.serverId,
        ...(catalogItemId ? { catalogItemId } : {})
      }, [422]);
      if (!result.eligibility) throw new Error("The corpse request was rejected without eligibility details.");
      return result;
    },
    async redeemGarageDino(context, garageDinoId) {
      return postJson<GarageBridgeActionResult>(`${baseUrl}/api/garage/redeem`, buildGarageRedeemPayload(context, garageDinoId));
    },
    async sellGarageDino(context, garageDinoId, reason) {
      await postJson(`${baseUrl}/api/garage/sell`, {
        userId: context.userId,
        garageDinoId,
        reason
      });
    },
    async discardGarageDino(context, garageDinoId, reason) {
      await deleteJson(`${baseUrl}/api/garage/dinos/${encodeURIComponent(garageDinoId)}`, { userId: context.userId, reason });
    },
    async renameGarageDino(context, garageDinoId, displayName) {
      await patchJson(`${baseUrl}/api/garage/dinos/${encodeURIComponent(garageDinoId)}/name`, { userId: context.userId, displayName });
    },
    async buyMarketplaceListing(context, listingId) {
      await postJson(`${baseUrl}/api/marketplace/listings/${encodeURIComponent(listingId)}/buy`, {
        buyerUserId: context.userId,
        actorDiscordRoleIds: context.roleIds
      });
    },
    async getAuctions(userId) { return getJson(`${baseUrl}/api/marketplace/auctions?userId=${encodeURIComponent(userId)}`); },
    async createAuction(payload) { return postJson(`${baseUrl}/api/marketplace/auctions`, payload); },
    async bidAuction(id, payload) { return postJson(`${baseUrl}/api/marketplace/auctions/${encodeURIComponent(id)}/bids`, payload); },
    async auctionAction(id, action, userId) { return postJson(`${baseUrl}/api/marketplace/auctions/${encodeURIComponent(id)}/${action}`, { userId }, [204]); },
    async createMarketplaceDinoListing(context, garageDinoId, payload) {
      await postJson(`${baseUrl}/api/marketplace/listings`, {
        sellerUserId: context.userId,
        itemType: "dino",
        itemId: garageDinoId,
        ...payload
      });
    },
    async cancelMarketplaceListing(context, listingId, reason) {
      await postJson(`${baseUrl}/api/marketplace/listings/${encodeURIComponent(listingId)}/cancel`, {
        actorUserId: context.userId,
        actorDiscordRoleIds: context.roleIds,
        reason
      });
    },
    async enterCommunityRaffle(userId, raffleId) {
      return postJson<{ alreadyEntered: boolean }>(`${baseUrl}/api/community-raffles/${encodeURIComponent(raffleId)}/entries`, { userId });
    },
    async buyStoreItem(context, catalogItemId, requestId, mutations) {
      return postJson<{ operation?: GarageBridgeActionResult["operation"] }>(`${baseUrl}/api/store/purchase`, {
        userId: context.userId,
        catalogItemId,
        requestId,
        ...(mutations ? { mutations } : {}),
        actorDiscordRoleIds: context.roleIds
      });
    },
    async getFriendTeleportCode(userId) {
      return getJson<FriendTeleportCodeState>(`${baseUrl}/api/store/friend-teleports/${encodeURIComponent(userId)}`);
    },
    async createFriendTeleportCode(userId) {
      return postJson<FriendTeleportCodeState>(`${baseUrl}/api/store/friend-teleports`, { userId });
    },
    async redeemFriendTeleportCode(userId, code) {
      return postJson<{ operation: GarageBridgeActionResult["operation"]; cost: number }>(`${baseUrl}/api/store/friend-teleports/redeem`, { userId, code });
    },
    async saveCorpseProduct(context, catalogItemId, payload) {
      await putJson(`${baseUrl}/api/store/corpse-products/${encodeURIComponent(catalogItemId)}`, {
        ...payload,
        actorUserId: context.userId,
        actorDiscordRoleIds: context.roleIds
      });
    },
    async saveStoreProduct(context, catalogItemId, payload) {
      await putJson(`${baseUrl}/api/store/catalog/${encodeURIComponent(catalogItemId)}`, {
        ...payload,
        actorUserId: context.userId,
        actorDiscordRoleIds: context.roleIds
      });
    },
    async claimWorkReward(context) {
      return postJson<WorkRewardResult>(`${baseUrl}/api/economy/work`, {
        userId: context.userId
      });
    },
    async getStickTransferStatus(userId) {
      return getJson<StickTransferStatus>(`${baseUrl}/api/economy/transfers/status?userId=${encodeURIComponent(userId)}`);
    },
    async searchStickTransferRecipients(userId, query) {
      const body = await getJson<{ recipients?: StickTransferRecipient[] }>(`${baseUrl}/api/economy/transfers/recipients?userId=${encodeURIComponent(userId)}&q=${encodeURIComponent(query)}&limit=6`);
      return body.recipients ?? [];
    },
    async sendSticks(payload) {
      return postJson<StickTransferResult>(`${baseUrl}/api/economy/transfers`, payload);
    },
    async getEventWheel(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<EventWheelState>(`${baseUrl}/api/gambling/wheel?${query.toString()}`);
    },
    async spinEventWheel(payload) {
      return postJson<EventWheelSpinResult>(`${baseUrl}/api/gambling/wheel/spin`, payload);
    },
    async claimPrimeToken(spinId, payload) {
      return postJson<{ operation?: GarageBridgeActionResult["operation"]; applied?: boolean; pending?: boolean; replayed?: boolean }>(`${baseUrl}/api/events/prime-tokens/${encodeURIComponent(spinId)}/claim`, payload);
    },
    async getSpecimenEggs(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<SpecimenEggInventory>(`${baseUrl}/api/events/specimen-eggs?${query.toString()}`);
    },
    async claimSpecimenEgg(spinId, payload) {
      return postJson(`${baseUrl}/api/events/specimen-eggs/${encodeURIComponent(spinId)}/claim`, payload);
    },
    async grantEventCredits(payload) {
      return postJson(`${baseUrl}/api/economy/event-credits/grant`, payload);
    },
    async grantStaffPoints(payload) {
      return postJson(`${baseUrl}/api/economy/staff-grants`, payload);
    },
    async getEventWheelConfig(serverId) {
      const body = await getJson<{ configuration: EventWheelConfigurationSummary }>(`${baseUrl}/api/gambling/wheel/config?serverId=${encodeURIComponent(serverId)}`);
      return body.configuration;
    },
    async saveEventWheelConfig(payload) {
      const body = await putJson<{ configuration: EventWheelConfigurationSummary }>(`${baseUrl}/api/gambling/wheel/config`, payload);
      return body.configuration;
    },
    async getEventJoinCampaign(serverId) {
      return getJson(`${baseUrl}/api/events/join-campaign?serverId=${encodeURIComponent(serverId)}`);
    },
    async saveEventJoinCampaign(payload) {
      return putJson(`${baseUrl}/api/events/join-campaign`, payload);
    },
    async getEventRewardLogs(serverId, limit = 200) {
      return getJson(`${baseUrl}/api/events/reward-logs?serverId=${encodeURIComponent(serverId)}&limit=${limit}`);
    },
    async getBlackjack(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<BlackjackState>(`${baseUrl}/api/gambling/blackjack?${query.toString()}`);
    },
    async dealBlackjack(payload) {
      return postJson<BlackjackHandResult>(`${baseUrl}/api/gambling/blackjack/hands`, payload);
    },
    async playBlackjack({ handId, ...payload }) {
      return postJson<BlackjackHandResult>(`${baseUrl}/api/gambling/blackjack/hands/${encodeURIComponent(handId)}/action`, payload);
    },
    async getBlackjackConfig(serverId) {
      const body = await getJson<{ configuration: BlackjackConfigurationSummary }>(`${baseUrl}/api/gambling/blackjack/config?serverId=${encodeURIComponent(serverId)}`);
      return body.configuration;
    },
    async saveBlackjackConfig(payload) {
      const body = await putJson<{ configuration: BlackjackConfigurationSummary }>(`${baseUrl}/api/gambling/blackjack/config`, payload);
      return body.configuration;
    },
    async getSlots(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<SlotsState>(`${baseUrl}/api/gambling/slots?${query.toString()}`);
    },
    async spinSlots(payload) {
      return postJson<SlotsResult>(`${baseUrl}/api/gambling/slots/spins`, payload);
    },
    async getSlotsConfig(serverId) {
      const body = await getJson<{ configuration: RouletteConfigurationSummary }>(`${baseUrl}/api/gambling/slots/config?serverId=${encodeURIComponent(serverId)}`);
      return body.configuration;
    },
    async saveSlotsConfig(payload) {
      const body = await putJson<{ configuration: RouletteConfigurationSummary }>(`${baseUrl}/api/gambling/slots/config`, payload);
      return body.configuration;
    },
    async getRoulette(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<RouletteState>(`${baseUrl}/api/gambling/roulette?${query.toString()}`);
    },
    async spinRoulette(payload) {
      return postJson<RouletteSpinResult>(`${baseUrl}/api/gambling/roulette/spins`, payload);
    },
    async getRouletteConfig(serverId) {
      const body = await getJson<{ configuration: RouletteConfigurationSummary }>(`${baseUrl}/api/gambling/roulette/config?serverId=${encodeURIComponent(serverId)}`);
      return body.configuration;
    },
    async saveRouletteConfig(payload) {
      const body = await putJson<{ configuration: RouletteConfigurationSummary }>(`${baseUrl}/api/gambling/roulette/config`, payload);
      return body.configuration;
    },
    async getRaptorRace(userId, serverId) {
      const query = new URLSearchParams({ userId, serverId });
      return getJson<RaptorRaceState>(`${baseUrl}/api/gambling/raptor-racing?${query.toString()}`);
    },
    async placeRaptorRaceBet(payload) {
      return postJson<RaptorRaceState>(`${baseUrl}/api/gambling/raptor-racing/bets`, payload);
    },
    async getRaptorRaceConfig(serverId) {
      const body = await getJson<{ configuration: RaptorRaceConfigurationSummary }>(`${baseUrl}/api/gambling/raptor-racing/config?serverId=${encodeURIComponent(serverId)}`);
      return body.configuration;
    },
    async saveRaptorRaceConfig(payload) {
      const body = await putJson<{ configuration: RaptorRaceConfigurationSummary }>(`${baseUrl}/api/gambling/raptor-racing/config`, payload);
      return body.configuration;
    },
    async createBugReport(payload) {
      return postJson<BugReportResult>(`${baseUrl}/api/bug-reports`, payload);
    },
    async disconnectDiscord() {
      await postJson(`${baseUrl}/api/identity/discord/disconnect`, {});
    },
    async logout() {
      await postJson(`${baseUrl}/api/identity/logout`, {});
    },
    async listGarageDinos(userId) {
      const body = await getJson<{ garage?: GarageRecordSummary[] }>(`${baseUrl}/api/garage/${encodeURIComponent(userId)}`);
      return body.garage ?? [];
    },
    async getGarageHistory(userId) {
      return getJson<GarageHistorySummary>(`${baseUrl}/api/garage/${encodeURIComponent(userId)}/history`);
    },
    async restoreGarageDino(payload) {
      await postJson(`${baseUrl}/api/garage/restore`, payload);
    },
    async restoreDeathSnapshot(deathEventId, payload) {
      await postJson(`${baseUrl}/api/garage/death-snapshots/${encodeURIComponent(deathEventId)}/restore`, payload);
    },
    async migrateGarageDino(payload) {
      return postJson(`${baseUrl}/api/garage/migrate`, payload);
    },
    async inspectDinoMutations(payload) {
      return postJson(`${baseUrl}/api/garage/mutations/inspect`, payload);
    },
    async correctDinoMutation(payload) {
      return postJson(`${baseUrl}/api/garage/mutations/correct`, payload);
    },
    async requestSystemControl(context, action, reason) {
      await postJson(`${baseUrl}/api/system/control`, buildSystemControlPayload(context, action, reason));
    },
    async getProfileByDiscordUserId(discordUserId) {
      const body = await getJson<{ profile?: PlayerProfileSummary }>(`${baseUrl}/api/profile/discord/${encodeURIComponent(discordUserId)}`);
      if (!body.profile) {
        throw new Error("Profile response was missing profile data.");
      }
      return body.profile;
    },
    async getProfileByLookup(lookup) {
      const body = await getJson<{ profile?: PlayerProfileSummary }>(`${baseUrl}/api/profile/lookup/${encodeURIComponent(lookup)}`);
      if (!body.profile) {
        throw new Error("Profile response was missing profile data.");
      }
      return body.profile;
    },
    async searchProfiles(query) {
      const body = await getJson<{ matches?: PlayerSearchMatch[] }>(`${baseUrl}/api/profile/search?q=${encodeURIComponent(query)}&limit=8`);
      return body.matches ?? [];
    },
    async resetCorpseCooldown(payload) {
      await postJson(`${baseUrl}/api/corpse-drops/cooldown/reset`, payload);
    },
    async listModerationCases(userId, roleIds) {
      const query = new URLSearchParams();
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      const suffix = query.toString() ? `?${query.toString()}` : "";
      const body = await getJson<{ cases?: ModerationCaseSummary[] }>(`${baseUrl}/api/moderation/cases/${encodeURIComponent(userId)}${suffix}`);
      return body.cases ?? [];
    },
    async listMyModerationStrikes(userId) {
      const body = await getJson<{ strikes?: PlayerModerationStrikeSummary[] }>(`${baseUrl}/api/moderation/strikes/self/${encodeURIComponent(userId)}`);
      return body.strikes ?? [];
    },
    async listModerationStrikes(userId, roleIds) {
      const query = new URLSearchParams();
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      const suffix = query.toString() ? `?${query.toString()}` : "";
      const body = await getJson<{
        strikes?: ModerationStrikeSummary[];
        roleActions?: ModerationStrikeRoleActions;
      }>(`${baseUrl}/api/moderation/strikes/${encodeURIComponent(userId)}${suffix}`);
      return {
        strikes: body.strikes ?? [],
        ...(body.roleActions ? { roleActions: body.roleActions } : {})
      };
    },
    async createModerationCase(payload) {
      await postJson(`${baseUrl}/api/moderation/cases`, payload);
    },
    async createModerationStrike(payload) {
      return postJson(`${baseUrl}/api/moderation/strikes`, payload);
    },
    async resolveModerationStrike(strikeId, payload) {
      return postJson(`${baseUrl}/api/moderation/strikes/${encodeURIComponent(strikeId)}/resolve`, payload);
    },
    async updateWeather(serverId, payload) {
      await postJson(`${baseUrl}/api/weather/${encodeURIComponent(serverId)}`, {
        ...payload,
        source: "manual"
      });
    },
    async listLedgerEntries(userId, roleIds, limit = 25) {
      const query = new URLSearchParams({ limit: String(limit) });
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      return getJson<{
        wallet: { points: number; premium: number };
        entries: LedgerEntrySummary[];
      }>(`${baseUrl}/api/economy/ledger/${encodeURIComponent(userId)}?${query.toString()}`);
    },
    async listGameServerLogSamples(roleIds, limit = 25) {
      const query = new URLSearchParams({ limit: String(limit) });
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      const body = await getJson<{ samples?: GameServerLogSample[] }>(`${baseUrl}/api/game-server/log-samples?${query.toString()}`);
      return body.samples ?? [];
    },
    async listAuditEvents(roleIds, options = {}) {
      const query = new URLSearchParams({ limit: String(options.limit ?? 25) });
      if (roleIds.length) query.set("actorDiscordRoleIds", roleIds.join(","));
      if (options.type) query.set("type", options.type);
      if (options.targetId) query.set("targetId", options.targetId);
      const body = await getJson<{ events?: AuditEventSummary[] }>(`${baseUrl}/api/audit-events?${query.toString()}`);
      return body.events ?? [];
    },
    async getDiscordLogRouting(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<DiscordLogRoutingSettings>(`${baseUrl}/api/discord-log-routing?${query.toString()}`);
    },
    async saveDiscordLogRoute(payload) {
      await putJson(`${baseUrl}/api/discord-log-routing`, payload);
    },
    async deleteDiscordLogRoute(routeId, payload) {
      await deleteJson(`${baseUrl}/api/discord-log-routing/${encodeURIComponent(routeId)}`, payload);
    },
    async previewDiscordLogRoute(payload) {
      await postJson(`${baseUrl}/api/discord-log-routing/preview`, payload);
    },
    async getCrossSpeciesChatConfig(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<CrossSpeciesChatSettings>(`${baseUrl}/api/cross-species-chat?${query.toString()}`);
    },
    async saveCrossSpeciesChatConfig(payload) {
      return putJson<CrossSpeciesChatSettings>(`${baseUrl}/api/cross-species-chat`, payload);
    },
    async getAmbientAiConfig(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<AmbientAiSettings>(`${baseUrl}/api/ambient-ai?${query.toString()}`);
    },
    async saveAmbientAiConfig(payload) {
      return putJson<AmbientAiSettings>(`${baseUrl}/api/ambient-ai`, payload);
    },
    async getCorpseCleanupConfig(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<CorpseCleanupSettings>(`${baseUrl}/api/corpse-cleanup?${query.toString()}`);
    },
    async saveCorpseCleanupConfig(payload) {
      return putJson<CorpseCleanupSettings>(`${baseUrl}/api/corpse-cleanup`, payload);
    },
    async getEcosystemZones(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<EcosystemZoneSettings>(`${baseUrl}/api/ecosystem-zones?${query.toString()}`);
    },
    async saveEcosystemZones(payload) {
      return putJson<EcosystemZoneSettings>(`${baseUrl}/api/ecosystem-zones`, payload);
    },
    async getStaffCredentials(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<StaffCredentialsSettings>(`${baseUrl}/api/staff-credentials?${query.toString()}`);
    },
    async saveStaffCredentials(payload) {
      return putJson<StaffCredentialsSettings>(`${baseUrl}/api/staff-credentials`, payload);
    },
    async getModerationPolicy(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<ModerationPolicySettings>(`${baseUrl}/api/moderation-policy?${query.toString()}`);
    },
    async saveModerationPolicy(payload) {
      return putJson<ModerationPolicySettings>(`${baseUrl}/api/moderation-policy`, payload);
    },
    async getGarageCapacityPolicy(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<GarageCapacityPolicySettings>(`${baseUrl}/api/garage-capacity-policy?${query.toString()}`);
    },
    async saveGarageCapacityPolicy(payload) {
      return putJson<GarageCapacityPolicySettings>(`${baseUrl}/api/garage-capacity-policy`, payload);
    },
    async saveWorkRewardPolicy(payload) {
      return putJson<WorkRewardPolicySettings>(`${baseUrl}/api/economy/work-policy`, payload);
    },
    async getPointBalancePolicy(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<PointBalancePolicySettings>(`${baseUrl}/api/economy/point-balance-policy?${query.toString()}`);
    },
    async savePointBalancePolicy(payload) {
      return putJson<PointBalancePolicySettings>(`${baseUrl}/api/economy/point-balance-policy`, payload);
    },
    async getDiscordPatreonRolePolicy(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<DiscordPatreonRolePolicySettings>(`${baseUrl}/api/economy/patreon-role-policy?${query.toString()}`);
    },
    async saveDiscordPatreonRolePolicy(payload) {
      return putJson<DiscordPatreonRolePolicySettings>(`${baseUrl}/api/economy/patreon-role-policy`, payload);
    },
    async getRuntimeReadiness() {
      return getJson<RuntimeReadinessSettings>(`${baseUrl}/ready`);
    },
    async getServerHealth(serverId, roleIds) {
      const query = new URLSearchParams({ serverId, actorDiscordRoleIds: roleIds.join(",") });
      return getJson<ServerHealthSettings>(`${baseUrl}/api/game-server/server-health?${query.toString()}`);
    },
    async getReleaseCentre() {
      return getJson<ReleaseCentreSettings>(`${baseUrl}/api/release-centre`);
    },
    async getGrowthCentre(days) {
      const query = new URLSearchParams({ days: String(days) });
      return getJson<GrowthCentreReport>(`${baseUrl}/api/growth-centre?${query.toString()}`);
    },
    async saveReleaseSignoff(payload) {
      return postJson<ReleaseCentreSettings>(`${baseUrl}/api/release-centre/signoffs`, payload);
    },
    async getCustomAiMap(serverId, roleIds) {
      const query = new URLSearchParams({ serverId, actorDiscordRoleIds: roleIds.join(",") });
      return getJson<CustomAiMapSettings>(`${baseUrl}/api/game-server/custom-ai-map?${query.toString()}`);
    },
    async getTelemetryHealth(serverId, roleIds) {
      const query = new URLSearchParams({ serverId, actorDiscordRoleIds: roleIds.join(","), limit: "50" });
      return getJson<TelemetryHealthSettings>(`${baseUrl}/api/telemetry/health?${query.toString()}`);
    },
    async listGameplayEvents(serverId, roleIds, filters = {}) {
      const query = new URLSearchParams({ serverId, actorDiscordRoleIds: roleIds.join(","), limit: String(filters.limit ?? 50) });
      for (const [key, value] of Object.entries(filters)) {
        if (value !== undefined && value !== "" && key !== "limit") query.set(key, String(value));
      }
      return getJson<GameplayEventPage>(`${baseUrl}/api/telemetry/events?${query.toString()}`);
    },
    async getTelemetryRetention(serverId, roleIds) {
      const query = new URLSearchParams({ serverId, actorDiscordRoleIds: roleIds.join(",") });
      const body = await getJson<{ policy: TelemetryRetentionSettings }>(`${baseUrl}/api/telemetry/retention?${query.toString()}`);
      return body.policy;
    },
    async saveTelemetryRetention(payload) {
      const body = await putJson<{ policy: TelemetryRetentionSettings }>(`${baseUrl}/api/telemetry/retention`, payload);
      return body.policy;
    },
    async getAccessControlPreview(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<AccessControlPreviewSettings>(`${baseUrl}/api/access-control/preview?${query.toString()}`);
    },
    async getPortalAccessSettings(roleIds) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(",") });
      return getJson<PortalAccessSettings>(`${baseUrl}/api/portal-access/settings?${query.toString()}`);
    },
    async savePortalAccessSettings(payload) {
      return putJson<PortalAccessSettings>(`${baseUrl}/api/portal-access/settings`, payload);
    },
    async exportConfigurationSnapshot(context) {
      const query = new URLSearchParams({
        actorUserId: context.userId,
        actorDiscordRoleIds: context.roleIds.join(",")
      });
      return getJson<ConfigurationSnapshotSettings>(`${baseUrl}/api/configuration-snapshots/export?${query.toString()}`);
    },
    async validateConfigurationSnapshot(context, snapshot) {
      return postJson<ConfigurationSnapshotValidation>(`${baseUrl}/api/configuration-snapshots/validate`, {
        actorUserId: context.userId,
        actorDiscordRoleIds: context.roleIds,
        snapshot
      });
    },
    async getStaffDuty(actorUserId, roleIds) {
      const query = new URLSearchParams({ actorUserId, actorDiscordRoleIds: roleIds.join(",") });
      const body = await getJson<{ duty?: StaffDutySettings }>(`${baseUrl}/api/staff-duty?${query.toString()}`);
      if (!body.duty) throw new Error("Staff duty response was missing duty state.");
      return body.duty;
    },
    async getStaffPerformance(actorUserId, roleIds, options) {
      const query = new URLSearchParams({ actorUserId, actorDiscordRoleIds: roleIds.join(","), periodDays: String(options.periodDays) });
      if (options.from) query.set("from", options.from);
      if (options.to) query.set("to", options.to);
      if (options.staffUserId) query.set("staffUserId", options.staffUserId);
      const body = await getJson<{ performance?: StaffPerformanceSummary }>(`${baseUrl}/api/staff-performance?${query.toString()}`);
      if (!body.performance) throw new Error("Staff performance response was missing its report.");
      return body.performance;
    },
    async setStaffDuty(payload) {
      const body = await putJson<{ duty?: StaffDutySettings }>(`${baseUrl}/api/staff-duty`, payload);
      if (!body.duty) throw new Error("Staff duty response was missing duty state.");
      return body.duty;
    },
    async listGameBridgeOperations(roleIds, options = {}) {
      const query = new URLSearchParams({ limit: String(options.limit ?? 25) });
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      if (options.status?.length) {
        query.set("status", options.status.join(","));
      }
      const body = await getJson<{
        operations?: GameBridgeOperationSummary[];
        summary?: GameBridgeOperationQueueSummary;
      }>(`${baseUrl}/api/game-server/bridge-operations?${query.toString()}`);
      return {
        operations: body.operations ?? [],
        ...(body.summary ? { summary: body.summary } : {})
      };
    },
    async getGameBridgeOperation(operationId, userId) {
      const query = new URLSearchParams({ userId });
      const body = await getJson<{ operation?: GameBridgeOperationSummary }>(
        `${baseUrl}/api/game-server/bridge-operations/${encodeURIComponent(operationId)}?${query.toString()}`
      );
      return body.operation;
    },
    async resolveGameBridgeOperation(operationId, payload) {
      const body = await postJson<{ operation?: GameBridgeOperationSummary }>(
        `${baseUrl}/api/game-server/bridge-operations/${encodeURIComponent(operationId)}/manual-resolution`,
        payload
      );
      if (!body.operation) {
        throw new Error("Bridge operation response was missing operation data.");
      }
      return body.operation;
    },
    async listSupportTickets(roleIds, options = {}) {
      const query = new URLSearchParams({ limit: String(options.limit ?? 25) });
      if (options.actorUserId) query.set("actorUserId", options.actorUserId);
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      if (options.query) query.set("query", options.query);
      if (options.type) {
        query.set("type", options.type);
      }
      if (options.status) {
        query.set("status", options.status);
      }
      if (options.lifecycle) query.set("lifecycle", options.lifecycle);
      if (options.priority) query.set("priority", options.priority);
      if (options.claimedByUserId) query.set("claimedByUserId", options.claimedByUserId);
      if (options.creatorUserId) query.set("creatorUserId", options.creatorUserId);
      if (options.accused) query.set("accused", options.accused);
      if (options.ruleCode) query.set("ruleCode", options.ruleCode);
      if (options.tag) query.set("tag", options.tag);
      if (options.resolutionType) query.set("resolutionType", options.resolutionType);
      if (options.createdFrom) query.set("createdFrom", options.createdFrom);
      if (options.createdTo) query.set("createdTo", options.createdTo);
      if (options.unassigned) query.set("unassigned", "true");
      if (options.overdue) query.set("overdue", "true");
      if (options.needsFollowup) query.set("needsFollowup", "true");
      if (options.cursor) query.set("cursor", options.cursor);
      const body = await getJson<{ tickets?: SupportTicketSummary[]; nextCursor?: string | null }>(`${baseUrl}/api/tickets?${query.toString()}`);
      return { tickets: body.tickets ?? [], nextCursor: body.nextCursor ?? null };
    },
    async updateSupportTicketTags(ticketId, payload) {
      const body = await postJson<{ ticket?: SupportTicketSummary }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/tags`, payload);
      if (!body.ticket) throw new Error("Ticket tag response was missing ticket data.");
      return body.ticket;
    },
    async bulkUpdateSupportTickets(payload) {
      return postJson(`${baseUrl}/api/tickets/bulk`, payload);
    },
    async getSupportTicketMetrics(roleIds, actorUserId, periodDays = 30) {
      const query = new URLSearchParams({ actorUserId, periodDays: String(periodDays) });
      if (roleIds.length) query.set("actorDiscordRoleIds", roleIds.join(","));
      const body = await getJson<{ metrics?: SupportTicketMetricsSummary }>(`${baseUrl}/api/tickets/metrics?${query.toString()}`);
      if (!body.metrics) throw new Error("Ticket metrics response was missing metrics data.");
      return body.metrics;
    },
    async getStaffTicketNotificationPreferences(actorUserId, roleIds) {
      const query = new URLSearchParams({ actorUserId });
      if (roleIds.length) query.set("actorDiscordRoleIds", roleIds.join(","));
      const body = await getJson<{ preference?: StaffTicketNotificationPreferenceSummary }>(`${baseUrl}/api/ticket-notifications/preferences?${query.toString()}`);
      if (!body.preference) throw new Error("Case notification preferences were missing from the response.");
      return body.preference;
    },
    async updateStaffTicketNotificationPreferences(payload) {
      const body = await putJson<{ preference?: StaffTicketNotificationPreferenceSummary }>(`${baseUrl}/api/ticket-notifications/preferences`, payload);
      if (!body.preference) throw new Error("Case notification preferences were missing from the response.");
      return body.preference;
    },
    async listStaffTicketNotifications(actorUserId, roleIds, limit = 30) {
      const query = new URLSearchParams({ actorUserId, limit: String(limit) });
      if (roleIds.length) query.set("actorDiscordRoleIds", roleIds.join(","));
      const body = await getJson<{ unreadCount?: number; notifications?: StaffTicketNotificationSummary[] }>(`${baseUrl}/api/ticket-notifications?${query.toString()}`);
      return { unreadCount: body.unreadCount ?? 0, notifications: body.notifications ?? [] };
    },
    async markStaffTicketNotificationRead(notificationId, actorUserId, roleIds) {
      await postJson(`${baseUrl}/api/ticket-notifications/${encodeURIComponent(notificationId)}/read`, {
        actorUserId,
        actorDiscordRoleIds: roleIds
      });
    },
    async markAllStaffTicketNotificationsRead(actorUserId, roleIds) {
      await postJson(`${baseUrl}/api/ticket-notifications/read-all`, {
        actorUserId,
        actorDiscordRoleIds: roleIds
      });
    },
    async listEventRewardNotifications(userId, serverId, limit = 20) {
      const query = new URLSearchParams({ userId, serverId, limit: String(limit) });
      const body = await getJson<{ unreadCount?: number; notifications?: EventRewardNotificationSummary[] }>(`${baseUrl}/api/events/reward-notifications?${query.toString()}`);
      return { unreadCount: body.unreadCount ?? 0, notifications: body.notifications ?? [] };
    },
    async markEventRewardNotificationRead(notificationId, userId) {
      await postJson(`${baseUrl}/api/events/reward-notifications/${encodeURIComponent(notificationId)}/read`, { userId });
    },
    async markAllEventRewardNotificationsRead(userId, serverId) {
      await postJson(`${baseUrl}/api/events/reward-notifications/read-all`, { userId, serverId });
    },
    async listMySupportTickets(actorUserId, limit = 25) {
      const query = new URLSearchParams({ actorUserId, limit: String(limit) });
      const body = await getJson<{ tickets?: SupportTicketSummary[] }>(`${baseUrl}/api/tickets/mine?${query.toString()}`);
      return body.tickets ?? [];
    },
    async getSupportTicketCapabilities() {
      return getJson<SupportTicketCapabilities>(`${baseUrl}/api/tickets/capabilities`);
    },
    async createSupportTicket(payload) {
      const body = await postJson<{ ticket?: SupportTicketSummary }>(`${baseUrl}/api/tickets`, payload);
      if (!body.ticket) throw new Error("Ticket response was missing ticket data.");
      return body.ticket;
    },
    async createStaffContactTicket(payload) {
      const body = await postJson<{ ticket?: SupportTicketSummary }>(`${baseUrl}/api/tickets/staff-contact`, payload);
      if (!body.ticket) throw new Error("Case response was missing ticket data.");
      return body.ticket;
    },
    async getSupportTicket(ticketId, actorUserId, roleIds) {
      const query = new URLSearchParams({ actorUserId });
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      const body = await getJson<{
        ticket?: SupportTicketSummary;
        messages?: SupportTicketMessageSummary[];
        accusedPlayers?: SupportTicketAccusedPlayerSummary[];
        reporterProfile?: CaseParticipantProfileSummary;
        claimedByDisplayName?: string;
        accusedProfiles?: CaseParticipantProfileSummary[];
        garageRestoreCandidates?: CaseGarageRestoreCandidateSummary[];
        canSendToOwner?: boolean;
        canViewOwnerCases?: boolean;
        evidence?: SupportTicketEvidenceSummary[];
        appealedStrike?: PlayerModerationStrikeSummary;
        logBundles?: SupportTicketLogBundleSummary[];
        evidenceUploadsEnabled?: boolean;
        watchers?: SupportTicketWatcherSummary[];
        rules?: SupportTicketRuleSummary[];
        transcript?: SupportTicketTranscriptSummary | null;
        discordRoom?: SupportTicketDiscordRoomSummary;
      }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}?${query.toString()}`);
      if (!body.ticket) {
        throw new Error("Ticket response was missing ticket data.");
      }

      return {
        ticket: body.ticket,
        messages: body.messages ?? [],
        accusedPlayers: body.accusedPlayers ?? [],
        ...(body.reporterProfile ? { reporterProfile: body.reporterProfile } : {}),
        ...(body.claimedByDisplayName ? { claimedByDisplayName: body.claimedByDisplayName } : {}),
        accusedProfiles: body.accusedProfiles ?? [],
        garageRestoreCandidates: body.garageRestoreCandidates ?? [],
        canSendToOwner: body.canSendToOwner === true,
        canViewOwnerCases: body.canViewOwnerCases === true,
        evidence: body.evidence ?? [],
        ...(body.appealedStrike ? { appealedStrike: body.appealedStrike } : {}),
        logBundles: body.logBundles ?? [],
        evidenceUploadsEnabled: body.evidenceUploadsEnabled === true,
        watchers: body.watchers ?? [],
        rules: body.rules ?? [],
        transcript: body.transcript ?? null,
        discordRoom: body.discordRoom ?? { configured: false, reporterLinked: false }
      };
    },
    async openSupportTicketDiscordRoom(ticketId, payload) {
      return postJson<{ channelId: string; url?: string; replayed: boolean }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/discord-room`, payload);
    },
    async addSupportTicketEvidence(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/evidence`, payload);
    },
    async uploadSupportTicketEvidence(ticketId, label, file) {
      const form = new FormData();
      form.append("label", label);
      form.append("file", file, file.name);
      await postForm(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/evidence/upload`, form);
    },
    async reviewSupportTicketEvidence(ticketId, evidenceId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/evidence/${encodeURIComponent(evidenceId)}/review`, payload);
    },
    async createSupportTicketLogBundle(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/log-bundles`, payload);
    },
    async getSupportTicketLogBundle(ticketId, bundleId) {
      return getJson<{ logBundle: SupportTicketLogBundleSummary; events: SupportTicketLogEventSummary[] }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/log-bundles/${encodeURIComponent(bundleId)}`);
    },
    async attachSupportTicketLogEvidence(ticketId, bundleId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/log-bundles/${encodeURIComponent(bundleId)}/evidence`, payload);
    },
    async addSupportTicketWatcher(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/watchers`, payload);
    },
    async removeSupportTicketWatcher(ticketId, payload) {
      await deleteJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/watchers`, payload);
    },
    async listRuleDefinitions(roleIds, options = {}) {
      const query = new URLSearchParams({ actorDiscordRoleIds: roleIds.join(","), limit: "200" });
      if (options.query) query.set("query", options.query);
      if (options.section) query.set("section", options.section);
      if (options.status) query.set("status", options.status);
      const body = await getJson<{ rules?: RuleDefinitionSummary[] }>(`${baseUrl}/api/rules?${query.toString()}`);
      return body.rules ?? [];
    },
    async createRuleDefinition(payload) {
      const body = await postJson<{ rule?: RuleDefinitionSummary }>(`${baseUrl}/api/rules`, payload);
      if (!body.rule) throw new Error("Rule response was missing rule data.");
      return body.rule;
    },
    async reviseRuleDefinition(ruleId, payload) {
      const body = await postJson<{ rule?: RuleDefinitionSummary }>(`${baseUrl}/api/rules/${encodeURIComponent(ruleId)}/revisions`, payload);
      if (!body.rule) throw new Error("Rule revision response was missing rule data.");
      return body.rule;
    },
    async changeRuleDefinitionStatus(ruleId, action, payload) {
      const body = await postJson<{ rule?: RuleDefinitionSummary }>(`${baseUrl}/api/rules/${encodeURIComponent(ruleId)}/${action}`, payload);
      if (!body.rule) throw new Error("Rule status response was missing rule data.");
      return body.rule;
    },
    async attachSupportTicketRule(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/rules`, payload);
    },
    async reviewSupportTicketRule(ticketId, ticketRuleId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/rules/${encodeURIComponent(ticketRuleId)}/review`, payload);
    },
    async attachSupportTicketAccused(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/accused`, payload);
    },
    async removeSupportTicketAccused(ticketId, accusedId, payload) {
      await deleteJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/accused/${encodeURIComponent(accusedId)}`, payload);
    },
    async getSupportTicketTranscript(ticketId, actorUserId, roleIds) {
      const query = new URLSearchParams({ actorUserId });
      if (roleIds.length) {
        query.set("actorDiscordRoleIds", roleIds.join(","));
      }
      const body = await getJson<{
        ticket?: SupportTicketSummary;
        transcript?: SupportTicketTranscriptSummary;
      }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/transcript?${query.toString()}`);
      if (!body.ticket || !body.transcript) {
        throw new Error("Transcript response was missing ticket data.");
      }

      return {
        ticket: body.ticket,
        transcript: body.transcript
      };
    },
    async replyToSupportTicket(ticketId, payload) {
      const body = await postJson<{ ticket?: SupportTicketSummary; message?: SupportTicketMessageSummary }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/messages`, payload);
      if (!body.ticket || !body.message) throw new Error("Ticket reply response was incomplete.");
      return { ticket: body.ticket, message: body.message };
    },
    async claimSupportTicket(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/claim`, payload);
    },
    async updateSupportTicketOperations(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/operations`, payload);
    },
    async updateSupportTicketStatus(ticketId, payload) {
      await postJson(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/status`, payload);
    },
    async closeSupportTicket(ticketId, payload) {
      const body = await postJson<{
        ticket?: SupportTicketSummary;
        moderation?: {
          strike: ModerationStrikeSummary;
          discordRoleSync: { applied: string[]; removed: string[]; errors: string[] };
          discordNotice: { delivered: boolean; error?: string };
        };
        compensation?: {
          ledgerEntry: { id: string; userId: string; amount: number; reason: string };
          requestedAmount: number;
          awardedAmount: number;
        };
      }>(`${baseUrl}/api/tickets/${encodeURIComponent(ticketId)}/close`, payload);
      if (!body.ticket) throw new Error("Ticket closure response was incomplete.");
      return {
        ticket: body.ticket,
        ...(body.moderation ? { moderation: body.moderation } : {}),
        ...(body.compensation ? { compensation: body.compensation } : {})
      };
    }
  };
}

async function getJson<T = unknown>(url: string, options: { timeoutMs?: number } = {}): Promise<T> {
  const response = await fetch(url, {
    ...(options.timeoutMs ? { signal: AbortSignal.timeout(options.timeoutMs) } : {})
  });
  const body = await response.json().catch(() => ({})) as unknown;

  if (!response.ok) {
    throw new PortalApiError(readError(body) ?? `API returned HTTP ${response.status}`, response.status);
  }

  return body as T;
}

async function postJson<T = unknown>(url: string, payload: unknown, acceptedStatuses: number[] = []): Promise<T> {
  const response = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await response.json().catch(() => ({})) as unknown;

  if (!response.ok && !acceptedStatuses.includes(response.status)) {
    throw new PortalApiError(readError(body) ?? `API returned HTTP ${response.status}`, response.status);
  }

  return body as T;
}

async function postForm<T = unknown>(url: string, payload: FormData): Promise<T> {
  const response = await fetch(url, { method: "POST", body: payload });
  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string };
    throw new PortalApiError(body.error ?? `Request failed with HTTP ${response.status}.`, response.status);
  }
  return await response.json() as T;
}

async function putJson<T = unknown>(url: string, payload: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await response.json().catch(() => ({})) as unknown;
  if (!response.ok) throw new PortalApiError(readError(body) ?? `API returned HTTP ${response.status}`, response.status);
  return body as T;
}

async function patchJson<T = unknown>(url: string, payload: unknown): Promise<T> {
  const response = await fetch(url, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload)
  });
  const body = await response.json().catch(() => ({})) as unknown;
  if (!response.ok) throw new PortalApiError(readError(body) ?? `API returned HTTP ${response.status}`, response.status);
  return body as T;
}

async function deleteJson<T = unknown>(url: string, payload: unknown): Promise<T> {
  const response = await fetch(url, { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify(payload) });
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => ({})) as unknown;
  if (!response.ok) throw new PortalApiError(readError(body) ?? `API returned HTTP ${response.status}`, response.status);
  return body as T;
}

function readError(body: unknown): string | undefined {
  if (body && typeof body === "object" && typeof (body as { error?: unknown }).error === "string") {
    const payload = body as { error: string; fields?: Array<{ path?: string; message?: string }> };
    const detail = payload.fields?.find((field) => typeof field.message === "string");
    return detail?.message
      ? `${payload.error} ${detail.path ? `${detail.path}: ` : ""}${detail.message}`
      : payload.error;
  }

  return undefined;
}
